import React, { FunctionComponent, ChangeEvent } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Formik, FieldProps } from 'formik';
import { Grid } from '@inplayer-org/inplayer-ui';

// utils
import { transformMetadataForUpdate } from 'utils/transformMetadataForUpdate';

// types
import { PreviewMetadataNames } from 'ducks/merchantDucks/items/types';

// constants
import Rules from 'restrictions/roles/constants/rules';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';
import useSharedObject from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedObject';

// actions
import { updateItemPreview, updateTemplatePreview, ItemType } from 'ducks/merchantDucks/items/actions';

// selectors
import { getItemDetails, getTemplateDetails } from 'ducks/merchantDucks/items/selectors';

// components
import Input from 'components/Fields/Input';
import UploadFile from 'components/UploadFile';
import TextEditor from 'components/TextEditor';
import AccordionFooter from 'components/AccordionFooter';
import {
  FullHeightForm,
  ContentContainer,
  CenterContentContainer,
  BlueTypography,
} from 'components/SharedStyledElements';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';

const { Container, Cell } = Grid;

export interface FormValues {
  preview_title: string;
  preview_description: string;
  preview_button_label?: string;
  paywall_cover_photo: File | string;
}

interface OwnProps {
  handleUpdatePreview: (previewData: { title?: string; description?: string; buttonLabel?: string }) => void;
  closePanel: () => void;
  isTemplate?: boolean;
  oldPreviewFields: {
    preview_title: string;
    preview_description: string;
    preview_button_label: string;
    paywall_cover_photo: string;
  };
  accordionLabel: string;
}

type Props = OwnProps;

const AssetPreviewForm: FunctionComponent<Props> = ({
  handleUpdatePreview,
  closePanel,
  isTemplate = false,
  oldPreviewFields,
  accordionLabel,
}) => {
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const isEditTemplate = pathname.includes('templates');

  const [, , resetPreviewFields] = useSharedObject('previewFields', {});

  const templateDetails = useSelector(getTemplateDetails);
  const itemDetails = useSelector(getItemDetails);
  const details = isEditTemplate ? (templateDetails as any) : itemDetails;
  let title = '';
  let description = '';
  let buttonLabel = '';

  const { metadata } = details;
  if (isEditTemplate) {
    if (metadata.length) {
      title = metadata?.find((item: { name: string }) => item.name === PreviewMetadataNames.PREVIEW_TITLE)?.value;
      description = metadata?.find((item: { name: string }) => item.name === PreviewMetadataNames.PREVIEW_DESCRIPTION)
        ?.value;
      buttonLabel = metadata?.find((item: { name: string }) => item.name === PreviewMetadataNames.PREVIEW_BUTTON)
        ?.value;
    }
  } else {
    const { preview_button_label, preview_description, preview_title } = metadata;
    title = preview_title;
    buttonLabel = preview_button_label;
    description = preview_description;
  }

  const initialValues: FormValues = {
    paywall_cover_photo: '',
    preview_button_label: buttonLabel,
    preview_title: title,
    preview_description: description,
  };

  const updatePreview = async (data: FormValues) => {
    const metadataForUpdate = transformMetadataForUpdate(data, oldPreviewFields);

    if (isTemplate) {
      await dispatch(updateTemplatePreview(metadataForUpdate));
      closePanel();
      return;
    }

    await dispatch(
      updateItemPreview({
        metadata: metadataForUpdate,
        itemType: ItemType.Asset,
      })
    );
    closePanel();
  };

  const closePanelHandler = () => {
    resetPreviewFields();
    closePanel();
  };

  return (
    <Formik onSubmit={updatePreview} initialValues={initialValues}>
      {({ isSubmitting, dirty }) => (
        <FullHeightForm>
          <FormAccordionHelper
            initialValues={initialValues}
            index={SectionType.Preview}
            closePanel={closePanelHandler}
          />
          <ContentContainer>
            <CenterContentContainer columns={1} rowGap="2rem">
              <Container columns={12} rowGap="1rem">
                <Cell width={12} middle>
                  <BlueTypography variant="h6">Title</BlueTypography>
                </Cell>
                <Cell width={12}>
                  <Field tag="text_editor_title" name="preview_title">
                    {({ field: { value, onChange, name } }: FieldProps) => (
                      <TextEditor
                        value={value}
                        readOnly={isReadOnly}
                        hideToolbarLink
                        onChange={(val: string) => {
                          onChange(name)(val);
                          handleUpdatePreview({
                            title: val,
                          });
                        }}
                      />
                    )}
                  </Field>
                </Cell>
                <Cell width={12} middle>
                  <BlueTypography variant="h6">Description</BlueTypography>
                </Cell>
                <Cell width={12}>
                  <Field tag="text_editor_description" name="preview_description">
                    {({ field: { value, onChange, name } }: FieldProps) => (
                      <TextEditor
                        value={value}
                        hideToolbarLink
                        readOnly={isReadOnly}
                        onChange={(val: string) => {
                          onChange(name)(val);
                          handleUpdatePreview({
                            description: val,
                          });
                        }}
                      />
                    )}
                  </Field>
                </Cell>
                <Cell width={12} middle>
                  <BlueTypography variant="h6">Button label</BlueTypography>
                </Cell>
                <Cell width={12}>
                  <Field tag="text_button_label" name="preview_button_label" type="text">
                    {({ field, form, meta }: FieldProps) => {
                      const { onChange, name } = field;
                      return (
                        <Input
                          field={field}
                          form={form}
                          meta={meta}
                          disabled={isReadOnly}
                          placeholder="Button label"
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            onChange(name)(e.target.value);
                            handleUpdatePreview({
                              buttonLabel: e.target.value,
                            });
                          }}
                        />
                      );
                    }}
                  </Field>
                </Cell>
              </Container>
              <BlueTypography variant="h6">Thumbnail</BlueTypography>
              <Field name="paywall_cover_photo">
                {({ field: { name }, form }: FieldProps) => (
                  <UploadFile uploadImage={(file: File) => form.setFieldValue(name, file)} disabled={isReadOnly} />
                )}
              </Field>
            </CenterContentContainer>
            <AccordionFooter
              accordionLabel={accordionLabel}
              handleCancel={closePanelHandler}
              disabled={isReadOnly || isSubmitting || !dirty}
            />
          </ContentContainer>
        </FullHeightForm>
      )}
    </Formik>
  );
};

export default AssetPreviewForm;
